<script setup lang="ts">

import { T } from '@tolgee/vue'
import { useMysteryBoxes, useOpenMysteryBox } from '@/composables/game/mystery-boxes'
import MysteryBoxCard from '@/components/MysteryBoxCard.vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { computed, ref } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import router from '@/router'
import { RouteName } from '@/routes'
import {
  ECOMMERCE_ITEM_PRICE_TYPE,
  type EcommerceItem,
  MYSTERY_BOX_TYPE,
  type MysteryBoxReward,
} from '@/types/shared/player-config'
import type { MysteryBoxItem, OpenedMysteryBoxReward } from '@/types/mystery-box'
import MysteryBoxRewardsModal from '@/components/modals/MysteryBoxRewardsModal.vue'
import MysteryBoxPurchaseModal from '@/components/modals/MysteryBoxPurchaseModal.vue'
import { syncPlayer } from '@/composables/game/player'
import { useConfetti } from '@/composables/game/confetti'
import MysteryBoxPurchaseSuccessfulModal from '@/components/modals/MysteryBoxPurchaseSuccessfulModal.vue'
import MysteryBoxOpeningOverlay from '@/components/overlays/MysteryBoxOpeningOverlay.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import MysteryBoxResultModal from '@/components/modals/MysteryBoxResultModal.vue'
import { useEcommerce } from '@/composables/game/ecommerce'
import { useToaster } from '@/composables/game/toasters'
import { useShop } from '@/composables/game/shop'
import MysteryBoxImage from '@/assets/images/mystery-boxes/mystery-boxes-main.webp'
import IWDMysteryBoxImage from '@/assets/images/periodic/IWD/IWD-mystery-box.gif'
import STPMysteryBoxImage from '@/assets/images/periodic/STP/STP-mystery-box.gif'
import IWDBackground from '@/assets/images/periodic/IWD/IWD-background.png'
import STPBackground from '@/assets/images/periodic/STP/STP-background.png'
import MysteryBoxBackground from '@/assets/images/mystery-bg.png'
import DefaultBOGOBackground from '@/assets/images/periodic/default-BOGO/default-BOGO-background.png'
import DefaultBOGXBackground from '@/assets/images/periodic/default-BOGX/default-BOGX-background.png'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { initialLiveOpsEvents } from '@/composables/game/live-ops-events'
import { useLiveOpsOverlay } from '@/composables/game/live-ops-overlay'
import DefaultBanner from '@/components/periodic/DefaultBanner.vue'
import type { BannerVariant } from '@/components/periodic/DefaultBanner.vue'
import DefaultTag from '@/components/periodic/DefaultTag.vue'
import type { TagVariant } from '@/components/periodic/DefaultTag.vue'

const route = useRoute()
const showCloseButton = computed(() => route.query.fromInit === 'true')
const { openLiveOpsOverlay } = useLiveOpsOverlay()

const {
  _mysteryBoxes,
  canHaveSale,
  unopenedMysteryBoxes,
  sortMysteryBoxRewards,
} = useMysteryBoxes()
const { showConfetti } = useConfetti()
const { openBox, openingBoxType } = useOpenMysteryBox()
const { purchase } = useEcommerce()
const { notify } = useToaster()
const {
  saleName,
  countdownForSaleEnds,
  isIWD,
  isDefaultDiscount,
  isDefaultBOGO,
  isSTP,
  isDefaultBOGX,
  saleValue,
  isSale,
} = useShop()
const { topNumber } = useFullScreenDimensions()

const mysteryBoxForRewardsModal = ref<MysteryBoxItem | null>(null)
const mysteryBoxForPurchaseModal = ref<MysteryBoxItem | null>(null)
const mysteryBoxForPurchaseSuccessModal = ref<null | { box: MysteryBoxItem, ecommerceItem: EcommerceItem }>(null)
const openedReward = ref<{ rewards: MysteryBoxReward[] | null, boxType: MYSTERY_BOX_TYPE | null }>({
  rewards: null,
  boxType: null,
})
const openedRewardItems = computed((): MysteryBoxReward[] | null => openedReward.value.rewards)
const showMysteryBoxRewardResultModal = ref(false)
const showOpeningAnimation = ref(false)
const loadingEcommerceItemId = ref<string | null>(null)

const topStyles = computed(() => {
  return { top: `-${topNumber.value + 24}px` }
})

const saleVariant = computed<BannerVariant | TagVariant>(() => {
  if (isDefaultBOGO.value) return 'BOGO'
  if (isDefaultBOGX.value) return 'BOGX'
  if (isDefaultDiscount.value) return 'discount'
  if (isIWD.value) return 'IWD'
  if (isSTP.value) return 'STP'
  return 'discount' // fallback
})

async function purchaseItem(box: MysteryBoxItem, item: EcommerceItem) {
  loadingEcommerceItemId.value = item.id
  try {
    const { paid } = await purchase({
      item: item,
      priceType: ECOMMERCE_ITEM_PRICE_TYPE.STAR,
      type: 'item',
    })
    if (paid) {
      await syncPlayer()
      await openMysteryBox(box.boxType)
      loadingEcommerceItemId.value = null
    } else {
      loadingEcommerceItemId.value = null
    }
  } catch (e) {
    loadingEcommerceItemId.value = null
  }
}

async function onPurchase(box: MysteryBoxItem, ecommerceItem: EcommerceItem) {
  if (!box || !ecommerceItem) return
  mysteryBoxForPurchaseModal.value = null
  mysteryBoxForPurchaseSuccessModal.value = { box, ecommerceItem }
  setTimeout(() => showConfetti({ spread: 220, count: 90 }), 100)
  await syncPlayer()
}

async function openMysteryBox(boxType: MYSTERY_BOX_TYPE) {
  resetOpenedReward()
  mysteryBoxForPurchaseSuccessModal.value = null
  showOpeningAnimation.value = true
  openedReward.value = { rewards: await openBox(boxType), boxType: boxType }
  if (!openedReward.value) {
    showOpeningAnimation.value = false
  }
}

async function finishBoxOpen() {
  showOpeningAnimation.value = false
  showMysteryBoxRewardResultModal.value = true
  notify({ title: 'Item added!', type: 'success' })
}

function resetOpenedReward() {
  openedReward.value = { rewards: null, boxType: null }
}

useWatchOverlays(computed(() => showOpeningAnimation.value))

onBeforeRouteLeave((_to, from) => {
  const next = from.query.fromInit ? initialLiveOpsEvents.value.shift() : undefined
  if (next) {
    openLiveOpsOverlay(next.id)
  }
})
</script>

<template>
  <div class="flex-1 flex flex-col w-full items-center px-4 pb-[100px]">
    <CrossCircleIcon
      v-if="showCloseButton"
      class="fixed top-3 right-3 size-8 text-white/[87%] z-[91] cursor-pointer"
      :style="{
        marginTop: `${topNumber}px`,
      }"
      @click="router.push({ name: RouteName.HOME })"
    />
    <img
      v-if="isIWD && canHaveSale"
      :src="IWDBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else-if="isSTP && canHaveSale"
      :src="STPBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else-if="isDefaultBOGO && canHaveSale"
      :src="DefaultBOGOBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else-if="isDefaultBOGX && canHaveSale"
      :src="DefaultBOGXBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <img
      v-else
      :src="MysteryBoxBackground"
      class="absolute w-full top-0 left-0 right-0 h-[400px] object-fill z-[1]"
    >
    <div class="w-full relative z-[2] mt-6 flex flex-col items-center">
      <div
        v-if="canHaveSale && isSale"
        class="absolute w-screen overflow-hidden h-[300px] -left-4 pointer-events-none flex justify-center"
        :style="topStyles"
      >
        <DefaultBanner
          :variant="saleVariant"
          :name="saleName"
          :countdown="countdownForSaleEnds"
        />
      </div>
      <DefaultTag
        v-if="canHaveSale && isSale"
        :variant="saleVariant"
        :amount="saleValue"
        class="absolute left-[calc(50%+70px)] z-[10]"
        :class="isDefaultBOGO || isDefaultBOGX ? 'top-[60px]' : 'top-[80px]'"
      />

      <div class="relative w-[237px] h-[177px] flex items-center justify-center -mb-7">
        <img
          v-if="isIWD && canHaveSale"
          :src="IWDMysteryBoxImage"
          class="w-full h-auto object-cover"
        >
        <img
          v-else-if="isSTP && canHaveSale"
          :src="STPMysteryBoxImage"
          class="w-full h-auto object-cover"
        >
        <img
          v-else
          :src="MysteryBoxImage"
          class="w-full h-auto object-cover"
        >
      </div>
      <div class="text-light-white text-[32px] leading-[32px] font-bold uppercase z-0">
        <T
          key-name="mystery_boxes.title"
          default-value="Mystery Boxes"
        />
      </div>
      <div class="text-center text-light-white/70 text-base font-medium -tracking-[0.32px]">
        <T
          key-name="mystery_boxes.description"
          default-value="Unlock surprises that boost your journey"
        />
      </div>
      <div class="mt-6 w-full flex flex-col gap-y-4">
        <MysteryBoxCard
          v-for="mysteryBox in _mysteryBoxes"
          :key="mysteryBox.boxType"
          class="scale-in"
          :class="{'pointer-events-none': openingBoxType === mysteryBox.boxType}"
          :name="mysteryBox.name"
          :image="mysteryBox.image"
          :description="mysteryBox.description"
          :ecommerce-items="mysteryBox.ecommerceItems"
          :unopened-count="unopenedMysteryBoxes[mysteryBox.boxType]"
          :is-loading="loadingEcommerceItemId !== null && mysteryBox.ecommerceItems.map(i => i.id).includes(loadingEcommerceItemId)"
          @click="mysteryBoxForRewardsModal = sortMysteryBoxRewards(mysteryBox)"
          @buy="mysteryBoxForPurchaseModal = mysteryBox"
          @buy-free="purchaseItem(mysteryBox, $event)"
          @open="openMysteryBox(mysteryBox.boxType)"
        />
      </div>
    </div>

    <MysteryBoxRewardsModal
      v-if="Boolean(mysteryBoxForRewardsModal)"
      :show="Boolean(mysteryBoxForRewardsModal)"
      :item="mysteryBoxForRewardsModal"
      @hide="mysteryBoxForRewardsModal = null"
    />

    <MysteryBoxPurchaseModal
      v-if="Boolean(mysteryBoxForPurchaseModal)"
      :show="Boolean(mysteryBoxForPurchaseModal)"
      :item="mysteryBoxForPurchaseModal"
      @hide="mysteryBoxForPurchaseModal = null"
      @on-purchase="onPurchase(mysteryBoxForPurchaseModal as MysteryBoxItem, $event)"
    />

    <MysteryBoxPurchaseSuccessfulModal
      v-if="Boolean(mysteryBoxForPurchaseSuccessModal)"
      :item="mysteryBoxForPurchaseSuccessModal"
      :show="Boolean(mysteryBoxForPurchaseSuccessModal)"
      @hide="mysteryBoxForPurchaseSuccessModal = null"
      @open="openMysteryBox"
    />

    <MysteryBoxOpeningOverlay
      v-if="showOpeningAnimation"
      :reward="openedRewardItems"
      :box-type="openedReward.boxType"
      @finish="finishBoxOpen"
    />

    <MysteryBoxResultModal
      v-if="showMysteryBoxRewardResultModal && openedReward.rewards && openedReward.boxType"
      :show="showMysteryBoxRewardResultModal"
      :item="openedReward as OpenedMysteryBoxReward"
      :unopened-boxes="unopenedMysteryBoxes"
      @open="showMysteryBoxRewardResultModal = false; openMysteryBox($event)"
      @hide="showMysteryBoxRewardResultModal = false; resetOpenedReward();"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/sales.scss';

.shadow-question-mark {
	filter: drop-shadow(0px 0px 22.857px #FFFFFF);
}
</style>
